<template>
  <div class="product-images">
    <div ref="swiper" class="swiper product-images__main-slider">
      <div class="swiper-wrapper">
        <div v-for="image in images" :key="image.id" class="swiper-slide product-images__image-item">
          <InnerImageZoom
            :src="image.uri"
            :hide-hint="true"
            :hide-close-button="true"
            :img-attrs="{ alt: image.alt, title: image.title, itemprop: 'image' }"
            zoom-type="click"
            class-name="product-images__image"
          />
        </div>
      </div>

      <div v-if="images.length > 1" class="product-images__navigation">
        <button type="button" class="button-reset swiper-button-prev">
          <ChevronLeft />
        </button>

        <button type="button" class="button-reset swiper-button-next">
          <ChevronRight />
        </button>
      </div>
    </div>

    <div v-if="images.length > 1" ref="thumbs" class="swiper product-images__thumbs">
      <div class="swiper-wrapper">
        <div v-for="image in images" :key="image.id" class="swiper-slide product-images__thumb-item">
          <img :src="image.uri" :alt="image.alt" :title="image.title" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAltTextAndTitleForProduct } from '@/helpers/sellerProduct/getAltTextAndTitleForProduct'
import { ChevronRight, ChevronLeft } from 'lucide-vue'
import Swiper, { Navigation, Thumbs } from 'swiper'
import 'swiper/css'
import InnerImageZoom from '@/components/common/InnerImageZoom.vue'

export default {
  name: 'ProductPageSlider',
  components: {
    ChevronRight,
    ChevronLeft,
    InnerImageZoom,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    activeSlideIndex: 0,
  }),
  computed: {
    images() {
      if (this.product.images.length === 0) {
        return [
          {
            uri: require('@/assets/images/icons/nophoto-medium.jpg'),
            uri_medium: require('@/assets/images/icons/nophoto-medium.jpg'),
            ...getAltTextAndTitleForProduct(this.product),
          },
        ]
      }

      return this.product.images.map(img => ({
        ...img,
        uri: img.image,
        uri_medium: img.image_medium,
        ...getAltTextAndTitleForProduct(this.product),
      }))
    },
  },
  mounted() {
    let thumbs

    if (this.images.length > 1) {
      thumbs = new Swiper(this.$refs.thumbs, {
        slidesPerView: 4,
        spaceBetween: 2,
        watchSlidesProgress: true,
        breakpoints: {
          1200: {
            direction: 'vertical',
          },
        },
      })
    }

    new Swiper(this.$refs.swiper, {
      modules: [Navigation, Thumbs],
      loop: true,
      slidesPerView: 1,
      navigation: {
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
      },
      thumbs: {
        swiper: thumbs,
      },
    })
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/common-imports.scss';

.product-images .product-images__navigation {
  position: absolute;
  left: 10px;
  bottom: 10px;
  z-index: 1;
  display: flex;
  gap: 8px;

  @include media-breakpoint-up(xl) {
    left: 20px;
    bottom: 20px;
  }
}

.product-images .swiper-button-prev,
.product-images .swiper-button-next {
  color: #fff;
  width: 24px;
  height: 24px;
  position: static;
  inset: initial;
  transform: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  @include media-breakpoint-up(xl) {
    width: 30px;
    height: 30px;
  }

  > svg {
    width: 14px;
    height: 14px;

    @include media-breakpoint-up(xl) {
      width: 18px;
      height: 18px;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '@/assets/scss/common-imports.scss';

.product-images {
  --corner-radius: 4px;

  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @include media-breakpoint-up(xl) {
    --corner-radius: 8px;
    flex-direction: row-reverse;
  }

  &__image-item {
    width: 100%;
    height: 174px;

    @include media-breakpoint-up(sm) {
      height: 200px;
    }

    @include media-breakpoint-up(md) {
      height: 174px;
    }

    @include media-breakpoint-up(xl) {
      height: 400px;
    }

    img {
    }
  }

  &__image {
    height: 100%;
    width: 100%;

    &::v-deep {
      .iiz__picture-wrap {
        height: 100%;
        width: 100%;
      }

      .iiz__picture {
        display: block;
        height: 100%;
        width: 100%;

        > img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: var(--corner-radius);
        }
      }
    }
  }

  &__main-slider {
    border-radius: var(--corner-radius);
  }

  &__main-slider,
  &__thumbs {
    min-width: 0;
    width: 100%;
  }

  &__thumbs {
    margin-top: 2px;
    display: none;

    @include media-breakpoint-up(md) {
      display: block;
    }

    @include media-breakpoint-up(xl) {
      margin-top: 0;
      margin-right: 2px;
      width: 85px;
      height: 400px;
    }
  }

  &__thumb-item {
    border-radius: var(--corner-radius);
    width: 100%;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      display: block;
      padding-top: 100%;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.46);
      transition: background-color 200ms ease-in-out;
    }

    &.swiper-slide-thumb-active::after {
      background-color: transparent;
    }

    img {
      display: block;
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}
</style>
